import React, { useEffect, useMemo, useState } from 'react';
import { authStorage } from '../../utils/storage';
import PageHead from '../general/PageHead';
import { RefreshCw, Search, CheckSquare, Square, X, UserCog, CheckCircle2, AlertCircle } from 'lucide-react';

// Slot key -> { label, pool endpoint }. Mirrors server/config/assignmentSlots.js.
const SLOTS = [
  { key: 'staff_rep', label: 'Staff Rep', poolEndpoint: '/api/staff' },
  { key: 'second_staff_rep', label: '2nd Staff Rep', poolEndpoint: '/api/staff' },
  { key: 'service_rep', label: 'Service Rep', poolEndpoint: '/api/service-reps' },
  { key: 'field_rep', label: 'Field Rep', poolEndpoint: '/api/field-reps' },
  { key: 'field_rep_2', label: 'Field Rep 2', poolEndpoint: '/api/field-reps' },
  { key: 'organizer', label: 'Organizer', poolEndpoint: '/api/organizers' },
];

const authHeaders = () => {
  const token = authStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const unitLabel = (u) =>
  `${u.LOCAL || ''} ${u.UNIT || ''} — ${u.NAME || ''}${u.TOWN ? `, ${u.TOWN}` : ''}`.replace(/^\s*—\s*/, '').trim();

export default function AssignmentsAdmin() {
  const [slotKey, setSlotKey] = useState('staff_rep');
  const [mode, setMode] = useState('assign'); // assign | remove | reassign
  const [units, setUnits] = useState([]);
  const [pool, setPool] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [selectedUnitIds, setSelectedUnitIds] = useState(() => new Set());
  const [heldUnitIds, setHeldUnitIds] = useState(() => new Set()); // Remove mode: the units the chosen employee holds
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null); // { kind: 'ok'|'err', text }

  const slot = SLOTS.find((s) => s.key === slotKey);

  // Load units once.
  useEffect(() => {
    fetch('/api/units', { headers: authHeaders() })
      .then((r) => r.json())
      .then((rows) => setUnits(Array.isArray(rows) ? rows : []))
      .catch(() => setUnits([]));
  }, []);

  // Load the pool whenever the slot changes; reset selections.
  useEffect(() => {
    setEmployeeId(''); setFromId(''); setToId(''); setSelectedUnitIds(new Set());
    fetch(slot.poolEndpoint, { headers: authHeaders() })
      .then((r) => r.json())
      .then((rows) => setPool(Array.isArray(rows) ? rows : []))
      .catch(() => setPool([]));
  }, [slotKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // In Remove mode, load the units the selected employee currently holds, then
  // pre-mark them all. The list below narrows to just these units.
  useEffect(() => {
    if (mode !== 'remove' || !employeeId) {
      setHeldUnitIds(new Set());
      return;
    }
    fetch(`/api/admin/assignments?slot=${slotKey}&employee_id=${encodeURIComponent(employeeId)}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => {
        const held = new Set((data.unit_ids || []).map(String));
        setHeldUnitIds(held);
        setSelectedUnitIds(new Set(held));
      })
      .catch(() => { setHeldUnitIds(new Set()); });
  }, [mode, employeeId, slotKey]);

  const filteredUnits = useMemo(() => {
    // Remove mode narrows the universe to the units the chosen employee holds.
    const base = mode === 'remove' ? units.filter((u) => heldUnitIds.has(String(u.id))) : units;
    const f = filter.trim().toLowerCase();
    if (!f) return base;
    return base.filter((u) => unitLabel(u).toLowerCase().includes(f));
  }, [units, filter, mode, heldUnitIds]);

  const toggleUnit = (id) => {
    const key = String(id);
    setSelectedUnitIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const selectAllFiltered = () => {
    setSelectedUnitIds((prev) => {
      const next = new Set(prev);
      filteredUnits.forEach((u) => next.add(String(u.id)));
      return next;
    });
  };
  const clearSelection = () => setSelectedUnitIds(new Set());

  const empName = (id) => {
    const e = pool.find((p) => String(p.EmployeeId) === String(id));
    return e ? (e.full_name || `${e.first_name || ''}`.trim() || `#${id}`) : '';
  };

  const summary = useMemo(() => {
    if (mode === 'assign') return employeeId ? `Assign ${empName(employeeId)} to ${selectedUnitIds.size} unit(s) as ${slot.label}` : '';
    if (mode === 'remove') return `Remove ${slot.label} from ${selectedUnitIds.size} unit(s)`;
    if (mode === 'reassign') return (fromId && toId) ? `Reassign all ${slot.label} units from ${empName(fromId)} to ${empName(toId)}` : '';
    return '';
  }, [mode, employeeId, fromId, toId, selectedUnitIds, slotKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = () => {
    if (busy) return false;
    if (mode === 'assign') return !!employeeId && selectedUnitIds.size > 0;
    if (mode === 'remove') return selectedUnitIds.size > 0;
    if (mode === 'reassign') return !!fromId && !!toId && fromId !== toId;
    return false;
  };

  const submit = async () => {
    if (!window.confirm(summary + '?')) return;
    setBusy(true); setToast(null);
    const body = { operation: mode, slot: slotKey };
    if (mode === 'assign') {
      body.employee_id = employeeId; body.employee_name = empName(employeeId);
      body.unit_ids = Array.from(selectedUnitIds).map((s) => Number(s));
    } else if (mode === 'remove') {
      body.unit_ids = Array.from(selectedUnitIds).map((s) => Number(s));
    } else {
      body.from_employee_id = fromId; body.to_employee_id = toId; body.to_employee_name = empName(toId);
    }
    try {
      const res = await fetch('/api/admin/assignments/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Request failed');
      setToast({ kind: 'ok', text: `Done — ${data.affected} unit(s) updated.` });
      clearSelection();
      setFromId(''); setToId('');
      const fresh = await fetch('/api/units', { headers: authHeaders() }).then((r) => r.json());
      setUnits(Array.isArray(fresh) ? fresh : units);
    } catch (err) {
      setToast({ kind: 'err', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const fieldLabel = {
    display: 'block', fontSize: 11.5, fontWeight: 600, letterSpacing: '.04em',
    textTransform: 'uppercase', color: 'var(--fg-2)', marginBottom: 7,
  };

  const empOptions = (placeholder) => (
    <>
      <option value="">{placeholder || '— select employee —'}</option>
      {pool.map((p) => (
        <option key={p.EmployeeId} value={p.EmployeeId}>{p.full_name || `#${p.EmployeeId}`}</option>
      ))}
    </>
  );

  const MODES = [
    { key: 'assign', label: 'Assign' },
    { key: 'remove', label: 'Remove' },
    { key: 'reassign', label: 'Reassign' },
  ];

  return (
    <>
      <style>{`
        @media (max-width: 900px) {
          .mua-grid-2 { grid-template-columns: 1fr !important; gap: 14px !important; }
        }
      `}</style>
      <PageHead
        title="Mass Unit Assignment"
        sub="Assign, remove, or reassign a rep across many bargaining units at once"
        actions={
          <button
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => {
              fetch('/api/units', { headers: authHeaders() }).then((r) => r.json())
                .then((rows) => setUnits(Array.isArray(rows) ? rows : [])).catch(() => {});
              fetch(slot.poolEndpoint, { headers: authHeaders() }).then((r) => r.json())
                .then((rows) => setPool(Array.isArray(rows) ? rows : [])).catch(() => {});
            }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        }
      />

      {toast && (
        <div
          className={`badge ${toast.kind === 'ok' ? 'badge-success' : 'badge-danger'}`}
          style={{ marginBottom: 16, padding: '8px 14px', fontSize: 13.5 }}
        >
          {toast.kind === 'ok' ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
          {toast.text}
        </div>
      )}

      {/* Step 1 — role + operation */}
      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="mua-grid-2" style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 320px) 1fr', gap: 22, alignItems: 'start' }}>
          <div>
            <label style={fieldLabel}>Role / Slot</label>
            <select className="dh-select" value={slotKey} onChange={(e) => setSlotKey(e.target.value)}>
              {SLOTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label style={fieldLabel}>Operation</label>
            <div className="pills">
              {MODES.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={`pill ${mode === m.key ? 'active' : ''}`}
                  onClick={() => { setMode(m.key); setSelectedUnitIds(new Set()); }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Step 2 — pick people */}
      {(mode === 'assign' || mode === 'remove') && (
        <div className="card card-pad" style={{ marginBottom: 16, maxWidth: 420 }}>
          <label style={fieldLabel}>
            {mode === 'remove' ? 'Employee — pre-marks the units they hold' : 'Employee to assign'}
          </label>
          <select className="dh-select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            {empOptions()}
          </select>
        </div>
      )}

      {mode === 'reassign' && (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="mua-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22, maxWidth: 720 }}>
            <div>
              <label style={fieldLabel}>From (current rep)</label>
              <select className="dh-select" value={fromId} onChange={(e) => setFromId(e.target.value)}>
                {empOptions('— select source —')}
              </select>
            </div>
            <div>
              <label style={fieldLabel}>To (new rep)</label>
              <select className="dh-select" value={toId} onChange={(e) => setToId(e.target.value)}>
                {empOptions('— select target —')}
              </select>
            </div>
          </div>
          {fromId && toId && fromId === toId && (
            <div className="tiny" style={{ color: 'var(--color-danger)', marginTop: 10 }}>
              From and To must be different people.
            </div>
          )}
        </div>
      )}

      {/* Step 3 — pick units (assign / remove only) */}
      {(mode === 'assign' || mode === 'remove') && (
        <div className="card" style={{ marginBottom: 16, overflow: 'hidden' }}>
          <div className="card-head">
            <h3>Units</h3>
            <span className="tiny muted" style={{ marginLeft: 10 }}>
              {selectedUnitIds.size} selected · {filteredUnits.length} shown
            </span>
            <div className="actions">
              <button className="btn btn-secondary btn-sm" onClick={selectAllFiltered}>
                <CheckSquare size={14} /> Select all filtered
              </button>
              <button className="btn btn-ghost btn-sm" onClick={clearSelection}>
                <X size={14} /> Clear
              </button>
            </div>
          </div>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
            <div className="search-box" style={{ maxWidth: 340 }}>
              <Search size={14} className="ic" />
              <input
                placeholder="Filter by local, name, or town…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {filteredUnits.length === 0 ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--fg-3)', fontSize: 13.5 }}>
                {mode === 'remove' && !employeeId
                  ? 'Select an employee to see the units they hold.'
                  : mode === 'remove' && heldUnitIds.size === 0
                    ? 'This employee isn’t assigned to any units for this slot.'
                    : 'No units match that filter.'}
              </div>
            ) : filteredUnits.map((u) => {
              const checked = selectedUnitIds.has(String(u.id));
              return (
                <label
                  key={u.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px',
                    borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', fontSize: 13.5,
                    color: 'var(--fg-1)', background: checked ? 'var(--bg-accent-soft)' : 'transparent',
                  }}
                >
                  {checked
                    ? <CheckSquare size={16} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                    : <Square size={16} style={{ color: 'var(--fg-3)', flexShrink: 0 }} />}
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleUnit(u.id)}
                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                  />
                  {unitLabel(u)}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 4 — confirm + apply */}
      <div
        className="card card-pad"
        style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'sticky', bottom: 16 }}
      >
        <UserCog size={20} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <div style={{ flex: 1, fontSize: 14, color: summary ? 'var(--fg-1)' : 'var(--fg-3)' }}>
          {summary || 'Select a role, operation, and targets to continue.'}
        </div>
        <button className="btn btn-primary" onClick={submit} disabled={!canSubmit()}>
          {busy ? 'Applying…' : 'Apply'}
        </button>
      </div>
    </>
  );
}
